import React, { Suspense, lazy, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { CreateAndEditDistributionPlan } from './DistributionPlan/CreateAndEditDistributionPlan'
import { type ApplyFiltersAndDispatchParams, type Row } from '../../components/CustomTable/types'
import ROUTES from '../../consts/paths'
import { useNavigate, useSearchParams } from 'react-router-dom'
import DeleteDistributionPlan from './DistributionPlan/DeleteDistributionPlan'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import PAGE_TITLES from '../../consts/titles'
import LoadingPageV2 from '../../pages/LoadingPageV2'
import { useQuery } from '@tanstack/react-query'
import { DistributionManager } from '../../APIManager'
import { notification } from '../../components/Notifications'

const AppTable = lazy(async () => await import('../../components/CustomTable'))

export const DistributionPlanTable: React.FC = () => {
  const navigate = useNavigate()
  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>({ pageSize: 5 })
  const [searchParams] = useSearchParams()
  const projectId = searchParams.get('projectId') ?? ''

  useEffect(() => {
    setParams({ pageSize: 5 })
  }, [projectId])

  const { data: distributionPlan = [], isLoading, isError, error } = useQuery({
    queryKey: ['distributionPlan', projectId, params],
    queryFn: async () => {
      return (await DistributionManager.getDistributionById(projectId, params)).data
    },
    enabled: !!projectId,
    retry: false,
    refetchOnMount: false
  })

  useEffect(() => {
    if (isError) {
      notification(error?.message ?? 'Error fetching distribution plans', { variant: 'error' })
    }
  }, [error])

  return (
    <Box display='flex' flexDirection='column' gap={6}>
      <Suspense fallback={<LoadingPageV2 title={PAGE_TITLES.DISTRIBUTION_SERIES} />}>
        <AppTable
          key={projectId}
          createAction={<CreateAndEditDistributionPlan projectId={projectId} />}
          list={distributionPlan ?? []}
          loading={isLoading}
          deleteAction={<DeleteDistributionPlan seriesId={distributionPlan?.distributionSeriesId} />}
          createActionTitle='Add Term'
          actionModalEntityDisplayField={'term.termName'}
          createButtonProps={{
            buttonType: ButtonType.Secondary,
            buttonSize: ButtonSize.Medium,
            disabled: !projectId
          }}
          featureName="Survey Project"
          onEditClick={(selectedRowData: Row) => {
            navigate(`${ROUTES.COURSE_AND_ENROLLMENT}/${selectedRowData?.data?.slotId as string}`)
          }}
          params={params}
          setParams={setParams}
          noDataComponentProps={{
            title: 'Start by creating a distribution plan',
            subTitle: 'Plan, coordinate, and execute Surveys by creating a Survey Project.'
          }}
        />
      </Suspense>
    </Box>
  )
}
